import React, { useRef, useState } from 'react';
import { Upload, Loader2, X, Type, Image as ImageIcon, Images, Film, Camera, BarChart3, Gauge, MousePointerClick, Paperclip, Quote, Minus, MoveVertical, LayoutTemplate, PanelBottom, Columns3, Share2, Video, Timer, HelpCircle, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { subir, errorMsg } from './newsletter.api';

export const TIPOS = {
  header: { txt: 'Encabezado', icon: LayoutTemplate, desc: 'Logo, título y bajada' },
  text: { txt: 'Texto', icon: Type, desc: 'Párrafos con negrita y links' },
  timelapse: { txt: 'Timelapse', icon: Film, desc: 'El último video de la obra, se actualiza solo' },
  photo: { txt: 'Foto de hoy', icon: Camera, desc: 'La última foto de la cámara' },
  progress: { txt: 'Avance de obra', icon: Gauge, desc: 'Porcentaje con barra' },
  kpis: { txt: 'Indicadores', icon: BarChart3, desc: 'Hasta 4 números destacados' },
  image: { txt: 'Imagen', icon: ImageIcon, desc: 'Render, plano o foto propia' },
  gallery: { txt: 'Galería', icon: Images, desc: 'Varias imágenes en grilla' },
  button: { txt: 'Botón', icon: MousePointerClick, desc: 'Un llamado a la acción' },
  files: { txt: 'Archivos', icon: Paperclip, desc: 'PDFs y documentos para descargar' },
  quote: { txt: 'Cita', icon: Quote, desc: 'Una frase destacada' },
  divider: { txt: 'Separador', icon: Minus, desc: 'Línea divisoria' },
  spacer: { txt: 'Espacio', icon: MoveVertical, desc: 'Aire entre bloques' },
  footer: { txt: 'Pie', icon: PanelBottom, desc: 'Datos de la empresa y redes' },
  columns: { txt: 'Columnas', icon: Columns3, desc: 'Unidades, amenities, 2 o 3 tarjetas' },
  social: { txt: 'Redes', icon: Share2, desc: 'Instagram, web, WhatsApp' },
  video: { txt: 'Video', icon: Video, desc: 'Recorrido o video de YouTube' },
  countdown: { txt: 'Cuenta regresiva', icon: Timer, desc: 'Días que faltan para la entrega' },
  faq: { txt: 'Preguntas', icon: HelpCircle, desc: 'Preguntas frecuentes' },
};

export const BLOQUE_NUEVO = {
  header: { kicker: '', title: 'Título', subtitle: '' },
  text: { title: '', text: 'Escribí acá. Usá **negrita** y [links](https://...).' },
  timelapse: { title: 'Así avanza la obra', text: '', buttonLabel: '▶  Ver el timelapse' },
  photo: { title: 'La obra hoy' },
  progress: { title: 'Avance de obra', percent: 50, note: '' },
  kpis: { title: '', items: [{ label: 'Avance', value: '50%' }, { label: 'Pisos', value: '5' }, { label: 'Entrega', value: '2027' }] },
  image: { url: '', caption: '', link: '' },
  gallery: { title: '', images: [] },
  button: { label: 'Más información', url: 'https://', align: 'center' },
  files: { title: 'Documentos', items: [] },
  quote: { text: '', author: '' },
  divider: {},
  spacer: { size: 24 },
  footer: { company: '', address: '', website: '', instagram: '', whatsapp: '', legal: '' },
  columns: { title: 'Unidades disponibles', items: [{ title: '2 ambientes', text: 'Desde 45 m²', imageUrl: '' }, { title: '3 ambientes', text: 'Desde 70 m²', imageUrl: '' }, { title: '4 ambientes', text: 'Desde 98 m²', imageUrl: '' }] },
  social: { title: 'Seguinos', instagram: '', facebook: '', linkedin: '', youtube: '', website: '', whatsapp: '' },
  video: { title: 'Recorrido virtual', url: '', thumbUrl: '', buttonLabel: '▶  Ver el video' },
  countdown: { title: 'Entrega de unidades', date: '', label: 'días para la entrega' },
  faq: { title: 'Preguntas frecuentes', items: [{ q: '¿Cuándo es la posesión?', a: '' }] },
};

const input = 'block w-full border border-gray-300 rounded-lg px-3 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

export function Campo({ label, valor, onChange, placeholder, area, tipo = 'text', ayuda }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold text-gray-700">{label}</span>
      {area
        ? <textarea value={valor ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${input} py-2 resize-y`} style={{ height: 110, lineHeight: 1.5 }} />
        : <input type={tipo} value={valor ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={input} style={{ height: 38 }} />}
      {ayuda && <span className="block text-[11px] text-gray-500">{ayuda}</span>}
    </label>
  );
}

function Subir({ label, valor, onChange, customerId, accept = 'image/*', multiple, onVarios }) {
  const ref = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const tomar = async (files) => {
    const lista = Array.from(files || []);
    if (!lista.length) return;
    setSubiendo(true);
    try {
      const subidos = [];
      for (const f of lista) {
        if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} pesa más de 15 MB`); continue; }
        subidos.push(await subir(f, customerId));
      }
      if (multiple) onVarios(subidos);
      else if (subidos[0]) onChange(subidos[0].url);
    } catch (e) { toast.error(errorMsg(e)); }
    setSubiendo(false);
  };
  return (
    <div className="space-y-1">
      {label && <span className="text-xs font-semibold text-gray-700">{label}</span>}
      <div className="flex items-center gap-2">
        {!multiple && valor && <img src={valor} alt="" className="w-14 h-14 object-cover rounded-lg border border-gray-200" />}
        <button type="button" onClick={() => ref.current?.click()} disabled={subiendo}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-800 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-60">
          {subiendo ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}<span>{subiendo ? 'Subiendo…' : multiple ? 'Subir archivos' : valor ? 'Cambiar' : 'Subir'}</span>
        </button>
        {!multiple && valor && <button type="button" onClick={() => onChange('')} className="text-xs text-red-600 hover:underline">Quitar</button>}
        <input ref={ref} type="file" accept={accept} multiple={!!multiple} className="hidden" onChange={(e) => { tomar(e.target.files); e.target.value = ''; }} />
      </div>
    </div>
  );
}

const Nota = ({ children }) => <div className="text-[11px] text-blue-800 bg-blue-50 rounded-lg px-3 py-2">{children}</div>;

/** Formulario de edición de un bloque. `set(campo, valor)` actualiza el bloque. */
export function EditorBloque({ b, set, ctx }) {
  const cid = ctx.customerId;
  switch (b.type) {
    case 'header':
      return (
        <div className="space-y-3">
          <Subir label="Logo" valor={b.logoUrl} onChange={(v) => set('logoUrl', v)} customerId={cid} />
          {!b.logoUrl && ctx.logo && <button type="button" onClick={() => set('logoUrl', ctx.logo)} className="text-xs font-semibold text-blue-700 hover:underline">Usar el logo de la obra</button>}
          <Campo label="Antetítulo" valor={b.kicker} onChange={(v) => set('kicker', v)} placeholder="Novedades de obra" />
          <Campo label="Título" valor={b.title} onChange={(v) => set('title', v)} ayuda="Podés usar {nombre}, {obra} y {empresa}." />
          <Campo label="Bajada" valor={b.subtitle} onChange={(v) => set('subtitle', v)} />
          <Subir label="Imagen principal (opcional)" valor={b.heroUrl} onChange={(v) => set('heroUrl', v)} customerId={cid} />
          <label className="flex items-center gap-2 text-xs text-gray-700"><input type="checkbox" checked={b.align === 'center'} onChange={(e) => set('align', e.target.checked ? 'center' : 'left')} />Centrado</label>
        </div>
      );
    case 'text':
      return (
        <div className="space-y-3">
          <Campo label="Título (opcional)" valor={b.title} onChange={(v) => set('title', v)} />
          <Campo label="Texto" area valor={b.text} onChange={(v) => set('text', v)} ayuda="**negrita**, *itálica*, [texto](https://link). Línea en blanco = párrafo nuevo. {nombre} pone el nombre de cada inversor." />
        </div>
      );
    case 'timelapse':
      return (
        <div className="space-y-3">
          <Nota>Se completa solo con el último timelapse de la cámara y su foto más reciente. En el mail va la foto con un botón que abre el video.</Nota>
          <Campo label="Título" valor={b.title} onChange={(v) => set('title', v)} />
          <Campo label="Texto (opcional)" area valor={b.text} onChange={(v) => set('text', v)} />
          <Campo label="Texto del botón" valor={b.buttonLabel} onChange={(v) => set('buttonLabel', v)} />
        </div>
      );
    case 'photo':
      return (
        <div className="space-y-3">
          <Nota>Muestra la foto más reciente de la cámara en el momento del envío.</Nota>
          <Campo label="Título" valor={b.title} onChange={(v) => set('title', v)} />
        </div>
      );
    case 'progress':
      return (
        <div className="space-y-3">
          <Campo label="Título" valor={b.title} onChange={(v) => set('title', v)} />
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-gray-700">Porcentaje: {b.percent || 0}%</span>
            <input type="range" min="0" max="100" value={b.percent || 0} onChange={(e) => set('percent', Number(e.target.value))} className="w-full" />
          </label>
          <Campo label="Nota (opcional)" valor={b.note} onChange={(v) => set('note', v)} />
        </div>
      );
    case 'kpis':
      return (
        <div className="space-y-3">
          <Campo label="Título (opcional)" valor={b.title} onChange={(v) => set('title', v)} />
          {[0, 1, 2, 3].map((i) => {
            const it = (b.items || [])[i] || { label: '', value: '' };
            const upd = (k, v) => { const items = [...(b.items || [])]; while (items.length <= i) items.push({ label: '', value: '' }); items[i] = { ...it, [k]: v }; set('items', items); };
            return (
              <div key={i} className="grid grid-cols-2 gap-2">
                <Campo label={`Valor ${i + 1}`} valor={it.value} onChange={(v) => upd('value', v)} placeholder="45%" />
                <Campo label="Descripción" valor={it.label} onChange={(v) => upd('label', v)} placeholder="Avance" />
              </div>
            );
          })}
        </div>
      );
    case 'image':
      return (
        <div className="space-y-3">
          <Subir label="Imagen" valor={b.url} onChange={(v) => set('url', v)} customerId={cid} />
          <Campo label="Epígrafe (opcional)" valor={b.caption} onChange={(v) => set('caption', v)} />
          <Campo label="Link al tocar la imagen (opcional)" valor={b.link} onChange={(v) => set('link', v)} placeholder="https://" />
        </div>
      );
    case 'gallery':
      return (
        <div className="space-y-3">
          <Campo label="Título (opcional)" valor={b.title} onChange={(v) => set('title', v)} />
          <div className="grid grid-cols-3 gap-2">
            {(b.images || []).map((u, i) => (
              <div key={u + i} className="relative">
                <img src={u} alt="" className="w-full aspect-square object-cover rounded-lg border border-gray-200" />
                <button type="button" onClick={() => set('images', b.images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 text-gray-700" aria-label="Quitar"><X size={12} /></button>
              </div>
            ))}
          </div>
          <Subir multiple customerId={cid} onVarios={(subidos) => set('images', [...(b.images || []), ...subidos.map((s) => s.url)].slice(0, 6))} />
          <span className="block text-[11px] text-gray-500">Hasta 6 imágenes.</span>
        </div>
      );
    case 'button':
      return (
        <div className="space-y-3">
          <Campo label="Texto" valor={b.label} onChange={(v) => set('label', v)} />
          <Campo label="Link" valor={b.url} onChange={(v) => set('url', v)} placeholder="https://wa.me/549..." ayuda="Puede ser tu web, un WhatsApp (https://wa.me/549…) o un formulario." />
          <label className="flex items-center gap-2 text-xs text-gray-700"><input type="checkbox" checked={b.align === 'center'} onChange={(e) => set('align', e.target.checked ? 'center' : 'left')} />Centrado</label>
        </div>
      );
    case 'files':
      return (
        <div className="space-y-3">
          <Campo label="Título" valor={b.title} onChange={(v) => set('title', v)} />
          {(b.items || []).map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={f.name} onChange={(e) => set('items', b.items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} className={input} style={{ height: 36 }} />
              {!f.url && <span className="text-[11px] text-amber-700 whitespace-nowrap">sin archivo</span>}
              <button type="button" onClick={() => set('items', b.items.filter((_, j) => j !== i))} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" aria-label="Quitar"><X size={14} /></button>
            </div>
          ))}
          <Subir multiple accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.zip" customerId={cid} onVarios={(subidos) => set('items', [...(b.items || []).filter((f) => f.url), ...subidos.map((s) => ({ name: s.nombre, url: s.url }))])} />
        </div>
      );
    case 'quote':
      return (
        <div className="space-y-3">
          <Campo label="Frase" area valor={b.text} onChange={(v) => set('text', v)} />
          <Campo label="Autor (opcional)" valor={b.author} onChange={(v) => set('author', v)} />
        </div>
      );
    case 'spacer':
      return (
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-gray-700">Alto: {b.size || 24}px</span>
          <input type="range" min="8" max="80" value={b.size || 24} onChange={(e) => set('size', Number(e.target.value))} className="w-full" />
        </label>
      );
    case 'footer':
      return (
        <div className="space-y-3">
          <Campo label="Empresa" valor={b.company} onChange={(v) => set('company', v)} />
          <Campo label="Dirección (opcional)" valor={b.address} onChange={(v) => set('address', v)} />
          <Campo label="Web" valor={b.website} onChange={(v) => set('website', v)} placeholder="https://" />
          <Campo label="Instagram" valor={b.instagram} onChange={(v) => set('instagram', v)} placeholder="https://instagram.com/…" />
          <Campo label="WhatsApp de contacto" valor={b.whatsapp} onChange={(v) => set('whatsapp', v)} placeholder="11 2345-6789" />
          <Campo label="Texto legal (opcional)" valor={b.legal} onChange={(v) => set('legal', v)} />
          <Nota>El link para darse de baja se agrega siempre, no se puede sacar.</Nota>
        </div>
      );
    case 'columns': {
      const items = b.items || [];
      const upd = (i, k, v) => set('items', items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
      return (
        <div className="space-y-3">
          <Campo label="Título (opcional)" valor={b.title} onChange={(v) => set('title', v)} />
          {items.map((it, i) => (
            <div key={i} className="border border-gray-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between"><span className="text-xs font-semibold text-gray-700">Columna {i + 1}</span><button type="button" onClick={() => set('items', items.filter((_, j) => j !== i))} className="p-1 text-red-500 hover:bg-red-50 rounded" aria-label="Quitar"><X size={13} /></button></div>
              <Subir valor={it.imageUrl} onChange={(v) => upd(i, 'imageUrl', v)} customerId={cid} />
              <Campo label="Título" valor={it.title} onChange={(v) => upd(i, 'title', v)} />
              <Campo label="Texto" valor={it.text} onChange={(v) => upd(i, 'text', v)} />
            </div>
          ))}
          {items.length < 3 && <button type="button" onClick={() => set('items', [...items, { title: '', text: '', imageUrl: '' }])} className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-700 border border-dashed border-blue-300 rounded-lg py-2 hover:bg-blue-50"><Plus size={13} /><span>Sumar columna</span></button>}
        </div>
      );
    }
    case 'social':
      return (
        <div className="space-y-3">
          <Campo label="Texto" valor={b.title} onChange={(v) => set('title', v)} />
          <Campo label="Instagram" valor={b.instagram} onChange={(v) => set('instagram', v)} placeholder="https://instagram.com/…" />
          <Campo label="Facebook" valor={b.facebook} onChange={(v) => set('facebook', v)} placeholder="https://facebook.com/…" />
          <Campo label="LinkedIn" valor={b.linkedin} onChange={(v) => set('linkedin', v)} placeholder="https://linkedin.com/…" />
          <Campo label="Web" valor={b.website} onChange={(v) => set('website', v)} placeholder="https://" />
          <Campo label="WhatsApp" valor={b.whatsapp} onChange={(v) => set('whatsapp', v)} placeholder="11 2345-6789" />
        </div>
      );
    case 'video':
      return (
        <div className="space-y-3">
          <Campo label="Título" valor={b.title} onChange={(v) => set('title', v)} />
          <Campo label="Link del video" valor={b.url} onChange={(v) => set('url', v)} placeholder="https://youtube.com/…" ayuda="Los mails no reproducen video: va la miniatura con un botón que lo abre." />
          <Subir label="Miniatura" valor={b.thumbUrl} onChange={(v) => set('thumbUrl', v)} customerId={cid} />
          <Campo label="Texto del botón" valor={b.buttonLabel} onChange={(v) => set('buttonLabel', v)} />
        </div>
      );
    case 'countdown':
      return (
        <div className="space-y-3">
          <Campo label="Título" valor={b.title} onChange={(v) => set('title', v)} />
          <Campo label="Fecha" tipo="date" valor={b.date} onChange={(v) => set('date', v)} ayuda="Cada envío calcula cuántos días faltan." />
          <Campo label="Texto debajo" valor={b.label} onChange={(v) => set('label', v)} />
        </div>
      );
    case 'faq': {
      const items = b.items || [];
      const upd = (i, k, v) => set('items', items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
      return (
        <div className="space-y-3">
          <Campo label="Título" valor={b.title} onChange={(v) => set('title', v)} />
          {items.map((it, i) => (
            <div key={i} className="border border-gray-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between"><span className="text-xs font-semibold text-gray-700">Pregunta {i + 1}</span><button type="button" onClick={() => set('items', items.filter((_, j) => j !== i))} className="p-1 text-red-500 hover:bg-red-50 rounded" aria-label="Quitar"><X size={13} /></button></div>
              <Campo label="Pregunta" valor={it.q} onChange={(v) => upd(i, 'q', v)} />
              <Campo label="Respuesta" area valor={it.a} onChange={(v) => upd(i, 'a', v)} />
            </div>
          ))}
          <button type="button" onClick={() => set('items', [...items, { q: '', a: '' }])} className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-700 border border-dashed border-blue-300 rounded-lg py-2 hover:bg-blue-50"><Plus size={13} /><span>Sumar pregunta</span></button>
        </div>
      );
    }
    default:
      return <div className="text-xs text-gray-500">Este bloque no tiene opciones.</div>;
  }
}
